import { useEffect, useRef, useState } from "react";
import { MapPin, Loader2, X, LocateFixed } from "lucide-react";
import { toast } from "sonner";
import { geocode, reverseGeocode } from "../lib/api";

export const LocationInput = ({
  label,
  placeholder,
  value,
  onSelect,
  testId,
  iconColor = "text-zinc-950",
  showLocateMe = false,
}) => {
  const [query, setQuery] = useState(value?.label || "");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    setQuery(value?.label || "");
  }, [value]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (v.trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    setOpen(true);
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await geocode(v);
        setSuggestions(results);
      } catch (err) {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 350);
  };

  const pick = (s) => {
    onSelect({ lat: s.lat, lon: s.lon, label: s.display_name });
    setQuery(s.display_name);
    setOpen(false);
  };

  const clear = () => {
    setQuery("");
    setSuggestions([]);
    onSelect(null);
    setOpen(false);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Your browser doesn't support geolocation");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const g = await reverseGeocode(latitude, longitude);
          onSelect({ lat: latitude, lon: longitude, label: g.display_name });
          setQuery(g.display_name);
          toast.success("Location detected");
        } catch (err) {
          onSelect({ lat: latitude, lon: longitude, label: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}` });
          setQuery(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
          toast.success("Location detected (address unavailable)");
        } finally {
          setLocating(false);
        }
      },
      (err) => {
        setLocating(false);
        const msg = err.code === 1 ? "Location permission denied" : "Couldn't get your location";
        toast.error(msg);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  };

  return (
    <div className="w-full" ref={wrapperRef}>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block">
          {label}
        </label>
        {showLocateMe && (
          <button
            type="button"
            onClick={useMyLocation}
            disabled={locating}
            data-testid={`${testId}-locate-me`}
            className="text-[10px] font-bold uppercase tracking-widest text-zinc-950 hover:text-green-700 inline-flex items-center gap-1 disabled:opacity-50 transition-colors"
          >
            {locating ? (
              <Loader2 className="animate-spin" size={11} />
            ) : (
              <LocateFixed size={11} />
            )}
            {locating ? "Locating…" : "Use my location"}
          </button>
        )}
      </div>
      <div className="relative flex items-center bg-zinc-50 rounded-2xl border border-zinc-200 focus-within:ring-2 focus-within:ring-zinc-950 focus-within:border-zinc-950 overflow-visible transition-all">
        <MapPin className={`ml-4 ${iconColor} shrink-0`} size={18} />
        <input
          type="text"
          value={query}
          onChange={handleChange}
          onFocus={() => query.length >= 3 && setOpen(true)}
          placeholder={placeholder}
          data-testid={testId}
          className="w-full bg-transparent px-3 py-4 text-base outline-none placeholder:text-zinc-400 text-zinc-950"
        />
        {loading && <Loader2 className="animate-spin text-zinc-400 mr-3" size={16} />}
        {!loading && query && (
          <button
            type="button"
            onClick={clear}
            aria-label="Clear"
            data-testid={`${testId}-clear`}
            className="mr-3 p-1 rounded-full hover:bg-zinc-200 transition-colors"
          >
            <X size={14} className="text-zinc-500" />
          </button>
        )}
        {open && (suggestions.length > 0 || loading) && (
          <div
            className="absolute top-[calc(100%+8px)] left-0 right-0 bg-white rounded-xl border border-zinc-200 shadow-xl max-h-72 overflow-y-auto z-50 flex flex-col py-2"
            data-testid={`${testId}-dropdown`}
          >
            {loading && suggestions.length === 0 && (
              <div className="px-4 py-3 text-sm text-zinc-500">Searching…</div>
            )}
            {suggestions.map((s, idx) => (
              <button
                key={`${s.lat}-${s.lon}-${idx}`}
                type="button"
                onClick={() => pick(s)}
                data-testid={`${testId}-suggestion-${idx}`}
                className="text-left px-4 py-3 hover:bg-zinc-50 transition-colors flex items-start gap-3 border-b border-zinc-100 last:border-0"
              >
                <MapPin size={14} className="mt-1 shrink-0 text-zinc-400" />
                <span className="text-sm text-zinc-900 leading-snug line-clamp-2">
                  {s.display_name}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
